import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { faToggleOff, faToggleOn } from '@seeqdev/icons/fa-pro-solid-svg-icons';

import { Icon } from '@seeqdev/qomponents';
import type { KeyboardEvent } from 'react';
import type { IconSize } from '@seeqdev/qomponents';

interface ToggleSwitchProps {
  isOn: boolean;
  onChange: (newValue: boolean) => void;
  label?: string;
  formattedLabel?: React.ReactNode;
  /**
   * Accessible name for the switch. Required for icon-only toggles (no label/title/formattedLabel),
   * otherwise the rendered label text supplies the name. Without it a label-less switch is announced
   * by screen readers as an unnamed switch.
   */
  ariaLabel?: string;
  title?: string;
  labelPosition?: 'left' | 'right';
  disabled?: boolean;
  testId?: string;
  xstyle?: stylex.StyleXStyles;
  extraClassNames?: string;
  iconSize?: IconSize;
}

/**
 * Generic two-state toggle button (on/off)
 */
export const ToggleSwitch = (props: ToggleSwitchProps) => {
  const {
    isOn,
    onChange,
    label,
    formattedLabel,
    ariaLabel,
    title,
    labelPosition = 'left',
    disabled = false,
    testId = 'toggleSwitch',
    extraClassNames,
    xstyle,
    iconSize = 'lg',
  } = props;

  const { t } = useTranslation();

  const hasLabelContent = Boolean(title || label || formattedLabel);
  const description = formattedLabel ? formattedLabel : label ? t(label) : null;
  const toggle = () => !disabled && onChange(!isOn);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ' && event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    toggle();
  };

  const labelArea = title ? (
    <div className={clsx('tw:flex tw:flex-col', { disabled })}>
      <span className="tw:font-bold">{t(title)}</span>
      {description && <span className="tw:text-muted">{description}</span>}
    </div>
  ) : (
    hasLabelContent && <span className={clsx({ disabled })}>{description}</span>
  );

  return (
    <div
      data-testid={testId}
      role="switch"
      aria-checked={isOn}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      {...stylex.props(!extraClassNames && toggleSwitchStyles.row, !title && toggleSwitchStyles.centered, xstyle)}
      className={clsx(
        stylex.props(!extraClassNames && toggleSwitchStyles.row, !title && toggleSwitchStyles.centered, xstyle)
          .className,
        extraClassNames,
        {
          disabledBehavior: disabled,
        },
      )}
      onClick={(e) => {
        e.stopPropagation();
        return toggle();
      }}
      onKeyDown={handleKeyDown}>
      {labelPosition === 'left' && labelArea}
      <Icon
        testId={`${testId}Icon`}
        icon={isOn ? faToggleOn : faToggleOff}
        size={iconSize}
        extraClassNames={clsx('cursorPointer', {
          ml7: hasLabelContent && labelPosition === 'left',
          mr7: hasLabelContent && labelPosition === 'right',
          disabled,
        })}
      />
      {labelPosition === 'right' && labelArea}
    </div>
  );
};

export const toggleSwitchStyles = stylex.create({
  row: { display: 'flex', flexDirection: 'row' },
  centered: { alignItems: 'center' },
});
